import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { inputCls } from "@/components/Modal";
import { theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import {
  ComposedChart, Bar, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const GRANS = [
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
  { id: "quarter", label: "Quarterly" },
  { id: "year", label: "Yearly" },
];

const kAxis = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v);
const tip = { background: "#fff", border: "1px solid rgba(28,28,30,0.08)", borderRadius: 8, fontSize: 12 };

function Stat({ label, value, tone }) {
  const c = tone === "amber" ? "text-agri-amber-dark" : tone === "danger" ? "text-agri-danger" : "text-agri-forest";
  return <div className="card-soft p-5"><div className="label-cap">{label}</div><div className={`font-heading text-2xl mt-2 tabular-nums ${c}`}>{value}</div></div>;
}

export default function ProfitLoss() {
  const [gran, setGran] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const { data } = useQuery({
    queryKey: ["pnl-series", gran, start, end],
    queryFn: () => api.get("/reports/pnl-series", {
      params: { granularity: gran, start: start || undefined, end: end || undefined },
    }).then((r) => r.data),
  });

  const periods = data?.periods ?? [];
  const totals = data?.totals ?? {};
  const pos = data?.position ?? {};

  return (
    <div className="space-y-6" data-testid="profit-loss-page">
      <div>
        <div className="label-cap">Analytics</div>
        <h1 className="font-heading text-3xl mt-1">Profit &amp; Growth</h1>
        <p className="text-sm text-agri-ink-2 mt-1">Profit &amp; loss over time, plus how your capital is growing.</p>
      </div>

      {/* Controls */}
      <div className="card-soft p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1 bg-agri-bg rounded-lg p-1">
          {GRANS.map((g) => (
            <button key={g.id} data-testid={`gran-${g.id}`} onClick={() => setGran(g.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${gran === g.id ? "bg-white text-agri-forest shadow-sm" : "text-agri-ink-2 hover:text-agri-ink"}`}>
              {g.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div><label className="block text-xs text-agri-ink-2 mb-1">From</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls + " w-40"} /></div>
        <div><label className="block text-xs text-agri-ink-2 mb-1">To</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls + " w-40"} /></div>
      </div>

      {/* P&L totals for the window */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Revenue" value={fmtMoney(totals.revenue || 0)} />
        <Stat label="Gross Profit" value={fmtMoney(totals.gross_profit || 0)} />
        <Stat label="Expenses" value={fmtMoney(totals.expenses || 0)} tone="danger" />
        <Stat label="Net Profit" value={fmtMoney(totals.net_profit || 0)} tone={(totals.net_profit || 0) >= 0 ? "amber" : "danger"} />
      </div>

      {/* Revenue vs net profit per period */}
      <div className="card-soft p-5">
        <div className="label-cap">Performance</div>
        <h3 className="font-heading text-xl mt-1 mb-3">Revenue &amp; net profit per period</h3>
        <div className="h-80">
          <ResponsiveContainer>
            <ComposedChart data={periods}>
              <CartesianGrid stroke="rgba(28,28,30,0.06)" strokeDasharray="3 3" />
              <XAxis dataKey="period" stroke="#4a4a4d" fontSize={11} />
              <YAxis stroke="#4a4a4d" fontSize={11} tickFormatter={kAxis} />
              <Tooltip contentStyle={tip} formatter={(v) => fmtMoney(v)} />
              <Legend iconType="circle" />
              <Bar name="Revenue" dataKey="revenue" fill="#1a3c2e" radius={[3, 3, 0, 0]} />
              <Bar name="Expenses" dataKey="expenses" fill="#e0c9a6" radius={[3, 3, 0, 0]} />
              <Line name="Net Profit" type="monotone" dataKey="net_profit" stroke="#d4891a" strokeWidth={2.5} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Investment / earnings growth */}
      <div className="card-soft p-5">
        <div className="label-cap">Growth</div>
        <h3 className="font-heading text-xl mt-1 mb-3">Cumulative net profit (earnings growth)</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={periods}>
              <defs>
                <linearGradient id="grow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1a3c2e" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#1a3c2e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(28,28,30,0.06)" strokeDasharray="3 3" />
              <XAxis dataKey="period" stroke="#4a4a4d" fontSize={11} />
              <YAxis stroke="#4a4a4d" fontSize={11} tickFormatter={kAxis} />
              <Tooltip contentStyle={tip} formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="cumulative_net" name="Cumulative profit" stroke="#1a3c2e" strokeWidth={2.5} fill="url(#grow)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Current capital position */}
      <div>
        <div className="label-cap mb-2">Capital tied up right now</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Stat label="Inventory @ Cost" value={fmtMoney(pos.inventory_cost || 0)} />
          <Stat label="Receivables (owed to you)" value={fmtMoney(pos.receivables || 0)} tone="amber" />
          <Stat label="Payables (you owe)" value={fmtMoney(pos.payables || 0)} tone="danger" />
          <Stat label="Net Position" value={fmtMoney(pos.net || 0)} />
        </div>
      </div>

      {/* Period breakdown table */}
      <div className="card-soft overflow-hidden">
        <div className="p-4 font-heading text-xl">Period breakdown</div>
        <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
          <table className="w-full text-sm rtable">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Period</th>
                <th className={thNumCls}>Revenue</th>
                <th className={thNumCls}>COGS</th>
                <th className={thNumCls}>Gross</th>
                <th className={thNumCls}>Expenses</th>
                <th className={thNumCls}>Net Profit</th>
                <th className={thNumCls}>Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-agri-ink-2 rt-full">No data in range.</td></tr>}
              {[...periods].reverse().map((p) => (
                <tr key={p.period} className={rowCls}>
                  <td data-label="Period" className={`${tdCls} rt-primary font-medium`}>{p.period}</td>
                  <td data-label="Revenue" className={tdNumCls}>{fmtMoney(p.revenue)}</td>
                  <td data-label="COGS" className={tdNumCls}>{fmtMoney(p.cogs)}</td>
                  <td data-label="Gross" className={tdNumCls}>{fmtMoney(p.gross_profit)}</td>
                  <td data-label="Expenses" className={tdNumCls}>{fmtMoney(p.expenses)}</td>
                  <td data-label="Net Profit" className={`${tdNumCls} font-semibold ${p.net_profit >= 0 ? "text-agri-forest" : "text-agri-danger"}`}>{fmtMoney(p.net_profit)}</td>
                  <td data-label="Cumulative" className={`${tdNumCls} font-semibold text-agri-forest`}>{fmtMoney(p.cumulative_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
